import './colors.css';
import './App.css';
import {
    Upload,
    Search,
    BarChart3,
    Sparkle,
    TrendingUp,
    Target,
    Eye,
    Wrench,
    MessageCircle,
    BriefcaseBusiness,
    Star,
    Focus,
    Loader2,
    Check,
    X,
    Flame,
    Info,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

function App() {
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [analysisData, setAnalysisData] = useState(null);
    const [comparisonData, setComparisonData] = useState(null);
    const [error, setError] = useState(null);
    const [resumeFile, setResumeFile] = useState(null);
    const [jobPostingFile, setJobPostingFile] = useState(null);
    const [resumeFileId, setResumeFileId] = useState(null);
    const [jobFileId, setJobFileId] = useState(null);
    const [activeTab, setActiveTab] = useState('resume');
    const [uploadMode, setUploadMode] = useState('analyze');
    const [roastData, setRoastData] = useState(null);
    const [showInfoBubble, setShowInfoBubble] = useState(false);
    const infoButtonRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (infoButtonRef.current && !infoButtonRef.current.contains(event.target)) {
                setShowInfoBubble(false);
            }
        };

        if (showInfoBubble) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showInfoBubble]);

    const getMetricIcon = (metric) => {
        const iconMap = {
            trajectory: TrendingUp,
            impact: Target,
            clarity: Eye,
            skills: Wrench,
            communication: MessageCircle,
            professionalism: BriefcaseBusiness,
            uniqueness: Star,
            focus: Focus,
        };
        const IconComponent = iconMap[metric] || Star;
        return <IconComponent size={16} style={{ marginRight: '0.2rem', transform: 'translateY(0.5px)' }} />;
    };

    const handleResumeUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            setError(null);
            setIsUploading(true);

            const formData = new FormData();
            formData.append('file', file);

            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                const response = await fetch(`${apiUrl}/upload`, {
                    method: 'POST',
                    body: formData,
                });

                if (!response.ok) {
                    throw new Error(`Upload failed: ${response.status}`);
                }

                const data = await response.json();
                setResumeFile(file);
                setResumeFileId(data.id);
                console.log('Resume uploaded:', data);
            } catch (err) {
                setError(err.message);
                console.error('Resume upload error:', err);
            } finally {
                setIsUploading(false);
            }
        }
    };

    const handleJobPostingUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            setError(null);
            setIsUploading(true);

            const formData = new FormData();
            formData.append('file', file);

            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                const response = await fetch(`${apiUrl}/upload`, {
                    method: 'POST',
                    body: formData,
                });

                if (!response.ok) {
                    throw new Error(`Upload failed: ${response.status}`);
                }

                const data = await response.json();
                setJobPostingFile(file);
                setJobFileId(data.id);
                console.log('Job posting uploaded:', data);
            } catch (err) {
                setError(err.message);
                console.error('Job posting upload error:', err);
            } finally {
                setIsUploading(false);
            }
        }
    };

    const handleAnalyze = async () => {
        if (!resumeFile || !resumeFileId) {
            setError('Please upload a resume first');
            return;
        }

        setIsLoading(true);
        setError(null);

        const requestBody = {
            resume_file_id: resumeFileId,
        };

        // Only include job_file_id if it exists
        if (jobFileId) {
            requestBody.job_file_id = jobFileId;
        }

        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const response = await fetch(`${apiUrl}/v2/analyze`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`Analysis failed: ${response.status}`);
            }

            const data = await response.json();
            setAnalysisData(data.resumeAnalysis);
            setComparisonData(data.comparisonResult || null);
            console.log('Analysis complete:', data);
        } catch (err) {
            setError(err.message);
            console.error('Analysis error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRoast = async () => {
        if (!resumeFile || !resumeFileId) {
            setError('Please upload a resume first');
            return;
        }

        setIsLoading(true);
        setError(null);

        const requestBody = {
            resume_file_id: resumeFileId,
        };

        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const response = await fetch(`${apiUrl}/roast`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`Roast failed: ${response.status}`);
            }

            const data = await response.json();
            setRoastData(data.roast);
            console.log('Roast complete:', data);
        } catch (err) {
            setError(err.message);
            console.error('Roast error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="App">
            <header className="top-bar">
                <h1 className="f-title">
                    <Sparkle size={24} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                    Noctowl
                </h1>
                <div className="info-button-container" ref={infoButtonRef}>
                    <button
                        className="info-button"
                        onClick={() => setShowInfoBubble(!showInfoBubble)}
                        aria-label="Information"
                    >
                        <Info size={20} />
                    </button>
                    {showInfoBubble && (
                        <div className="info-bubble">
                            This analysis is powered by AI and might not be 100% accurate. Please use the insights
                            as guidance.
                        </div>
                    )}
                </div>
            </header>
            <main className="main-content">
                <div className="cards-container">
                    <div className="card upload-card">
                        <div className="tabs-container">
                            <button
                                className={`tab-button ${uploadMode === 'analyze' ? 'active' : ''}`}
                                onClick={() => setUploadMode('analyze')}
                            >
                                Analyze Resume
                            </button>
                            <button
                                className={`tab-button ${uploadMode === 'roast' ? 'active' : ''}`}
                                onClick={() => setUploadMode('roast')}
                            >
                                Roast Resume
                            </button>
                        </div>
                        <p className="description">
                            {uploadMode === 'analyze'
                                ? 'Upload your resume to get detailed insights and analysis. Optionally upload a job posting for compatibility analysis.'
                                : 'Upload your resume to get a brutally honest roast of your qualifications.'}
                        </p>
                        <div className="upload-container">
                            <div className="file-upload-section">
                                <input
                                    type="file"
                                    id="resume-upload"
                                    accept=".pdf,.doc,.docx"
                                    onChange={handleResumeUpload}
                                    style={{ display: 'none' }}
                                    disabled={isLoading || isUploading}
                                />
                                <label
                                    htmlFor="resume-upload"
                                    className={`upload-button ${isLoading || isUploading ? 'disabled' : ''}`}
                                    style={{ cursor: isLoading || isUploading ? 'not-allowed' : 'pointer' }}
                                >
                                    <Upload size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                                    <span
                                        style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '250px',
                                            display: 'inline-block',
                                        }}
                                    >
                                        {isUploading ? 'Uploading...' : resumeFile ? resumeFile.name : 'Upload Resume (PDF)'}
                                    </span>
                                </label>
                                {resumeFile && (
                                    <span className="file-status">
                                        <Check size={20} />
                                    </span>
                                )}
                            </div>

                            {uploadMode === 'analyze' && (
                                <div className="file-upload-section">
                                    <input
                                        type="file"
                                        id="job-posting-upload"
                                        accept=".pdf,.doc,.docx,.txt"
                                        onChange={handleJobPostingUpload}
                                        style={{ display: 'none' }}
                                        disabled={isLoading || isUploading}
                                    />
                                    <label
                                        htmlFor="job-posting-upload"
                                        className={`upload-button secondary ${isLoading || isUploading ? 'disabled' : ''}`}
                                        style={{ cursor: isLoading || isUploading ? 'not-allowed' : 'pointer' }}
                                    >
                                        <Upload size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                                        <span
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: '250px',
                                                display: 'inline-block',
                                            }}
                                        >
                                            {isUploading
                                                ? 'Uploading...'
                                                : jobPostingFile
                                                  ? jobPostingFile.name
                                                  : 'Upload Job Posting (Optional)'}
                                        </span>
                                    </label>
                                    {jobPostingFile && (
                                        <span className="file-status">
                                            <Check size={20} />
                                        </span>
                                    )}
                                </div>
                            )}

                            {!isLoading &&
                                ((uploadMode === 'analyze' && !analysisData) ||
                                    (uploadMode === 'roast' && !roastData)) && (
                                    <div className="action-buttons">
                                        {uploadMode === 'analyze' ? (
                                            <button
                                                onClick={handleAnalyze}
                                                className={`analyze-button ${!resumeFile || isUploading ? 'disabled' : ''}`}
                                                disabled={!resumeFile || isUploading}
                                            >
                                                <Search size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                                                Analyze
                                            </button>
                                        ) : (
                                            <button
                                                onClick={handleRoast}
                                                className={`roast-button ${!resumeFile || isUploading ? 'disabled' : ''}`}
                                                disabled={!resumeFile || isUploading}
                                            >
                                                <Flame size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                                                Roast
                                            </button>
                                        )}
                                    </div>
                                )}
                            {error && <p className="error-message">{error}</p>}
                        </div>
                    </div>
                    {(analysisData || roastData || isLoading) && (
                        <div className="card preview-card">
                            <h2 className="f-title">
                                {isLoading ? (
                                    <>
                                        <div className="loading-spinner">
                                            <Loader2 size={32} className="spinner" />{' '}
                                        </div>
                                        {uploadMode === 'roast'
                                            ? 'Roasting your resume...'
                                            : 'Analyzing your resume...'}
                                    </>
                                ) : (
                                    <>
                                        <BarChart3
                                            size={28}
                                            style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}
                                        />
                                        {uploadMode === 'roast' ? 'Roast Results' : 'Analysis Results'}
                                    </>
                                )}
                            </h2>
                            {uploadMode === 'roast' && roastData && !isLoading && (
                                <div className="tab-content">
                                    <div className="summary-section">
                                        <p className="summary-text" style={{ whiteSpace: 'pre-wrap' }}>
                                            {roastData}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {uploadMode === 'analyze' && (analysisData || comparisonData) && !isLoading && (
                                <>
                                    <div className="tabs-container">
                                        <button
                                            className={`tab-button ${activeTab === 'resume' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('resume')}
                                        >
                                            Resume Analysis
                                        </button>
                                        <button
                                            className={`tab-button ${activeTab === 'compatibility' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('compatibility')}
                                            disabled={!comparisonData}
                                        >
                                            Job Compatibility
                                        </button>
                                    </div>

                                    {activeTab === 'resume' && analysisData && (
                                        <div className="tab-content">
                                            {analysisData?.metrics && (
                                                <div className="overall-score-section">
                                                    <h3 className="overall-score-title">Overall Score</h3>
                                                    <p className="overall-score-value">
                                                        {(() => {
                                                            const scores = Object.values(analysisData.metrics).map(
                                                                (metric) => metric.score,
                                                            );
                                                            const average =
                                                                scores.reduce((sum, score) => sum + score, 0) /
                                                                scores.length;
                                                            return average.toFixed(1);
                                                        })()}
                                                        /10
                                                    </p>
                                                </div>
                                            )}
                                            {analysisData?.short_summary && (
                                                <div className="summary-section">
                                                    <h3 className="summary-title">Summary</h3>
                                                    <p className="summary-text">{analysisData.short_summary}</p>
                                                </div>
                                            )}
                                            <div className="metrics-rows">
                                                {Object.entries(analysisData?.metrics || {}).map(([metric, data]) => (
                                                    <div key={metric} className="metric-row">
                                                        <div className="metric-left">
                                                            <span className="metric-name f-title">
                                                                {getMetricIcon(metric)}
                                                                {metric.charAt(0).toUpperCase() + metric.slice(1)}
                                                            </span>
                                                            <span className="metric-score">{data.score}/10</span>
                                                        </div>
                                                        <div className="metric-reason">
                                                            {data.reason.charAt(0).toUpperCase() + data.reason.slice(1)}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            {analysisData?.suggestions && (
                                                <div className="suggestions-section">
                                                    <h3 className="suggestions-title">Suggestions</h3>
                                                    <ul className="suggestions-list">
                                                        {analysisData.suggestions.map((suggestion, index) => (
                                                            <li key={index} className="suggestion-item">
                                                                {suggestion}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {activeTab === 'compatibility' && comparisonData && (
                                        <div className="tab-content">
                                            <div className="overall-score-section">
                                                <h3 className="overall-score-title">Match Score</h3>
                                                <p className="overall-score-value">{comparisonData.match_score}/10</p>
                                            </div>

                                            {comparisonData.summary && (
                                                <div className="summary-section">
                                                    <h3 className="summary-title">Summary</h3>
                                                    <p className="summary-text">{comparisonData.summary}</p>
                                                </div>
                                            )}

                                            <div className="metrics-rows">
                                                {comparisonData.experience_alignment && (
                                                    <div className="metric-row">
                                                        <div className="metric-left">
                                                            <span className="metric-name f-title">
                                                                {getMetricIcon('trajectory')}
                                                                Experience
                                                            </span>
                                                            <span className="metric-score">
                                                                {comparisonData.experience_alignment.score}/10
                                                            </span>
                                                        </div>
                                                        <div className="metric-reason">
                                                            {comparisonData.experience_alignment.reason}
                                                        </div>
                                                    </div>
                                                )}
                                                {comparisonData.skill_relevance && (
                                                    <div className="metric-row">
                                                        <div className="metric-left">
                                                            <span className="metric-name f-title">
                                                                {getMetricIcon('skills')}
                                                                Skills
                                                            </span>
                                                            <span className="metric-score">
                                                                {comparisonData.skill_relevance.score}/10
                                                            </span>
                                                        </div>
                                                        <div className="metric-reason">
                                                            {comparisonData.skill_relevance.reason}
                                                        </div>
                                                    </div>
                                                )}
                                                {comparisonData.cultural_fit && (
                                                    <div className="metric-row">
                                                        <div className="metric-left">
                                                            <span className="metric-name f-title">
                                                                {getMetricIcon('professionalism')}
                                                                Cultural Fit
                                                            </span>
                                                            <span className="metric-score">
                                                                {comparisonData.cultural_fit.score}/10
                                                            </span>
                                                        </div>
                                                        <div className="metric-reason">
                                                            {comparisonData.cultural_fit.reason}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {comparisonData.key_matches && comparisonData.key_matches.length > 0 && (
                                                <div className="key-matches-section">
                                                    <h3 className="section-title">Key Matches</h3>
                                                    <ul className="matches-list">
                                                        {comparisonData.key_matches.map((match, index) => (
                                                            <li key={index} className="match-item positive">
                                                                <Check size={16} className="match-icon" />
                                                                {match}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {comparisonData.missing_skills && comparisonData.missing_skills.length > 0 && (
                                                <div className="missing-skills-section">
                                                    <h3 className="section-title">Missing Skills</h3>
                                                    <ul className="skills-list">
                                                        {comparisonData.missing_skills.map((skill, index) => (
                                                            <li key={index} className="skill-item missing">
                                                                <X size={16} className="missing-icon" />
                                                                {skill}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {comparisonData.recommendations &&
                                                comparisonData.recommendations.length > 0 && (
                                                    <div className="suggestions-section">
                                                        <h3 className="suggestions-title">Recommendations</h3>
                                                        <ul className="suggestions-list">
                                                            {comparisonData.recommendations.map(
                                                                (recommendation, index) => (
                                                                    <li key={index} className="suggestion-item">
                                                                        {recommendation}
                                                                    </li>
                                                                ),
                                                            )}
                                                        </ul>
                                                    </div>
                                                )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}

export default App;
